import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

// Pages publiques autorisées pour le breadcrumb (SEO-friendly)
const PUBLIC_PAGES = [
  // Pages principales
  'office', 'resto', 'pricing', 'compare', 'contact',
  // Pages informatives  
  'about', 'team', 'careers', 'blog', 'press',
  // Support
  'support',
  // Légales
  'cgv', 'rgpd', 'ai-policy'
];

// 🌍 Traductions des segments de breadcrumb par langue
const getBreadcrumbTranslations = (lang: string) => {
  const translations: Record<string, Record<string, string>> = {
    'en': {
      'home': 'Home',
      'office': 'MetaSaaS for Office',
      'resto': 'MetaSaaS for Restaurants', 
      'pricing': 'Pricing',
      'compare': 'Compare Solutions',
      'contact': 'Contact',
      'about': 'About MetaSaaS',
      'careers': 'Careers',
      'blog': 'Blog',
      'press': 'Press',
      'team': 'Team',
      'support': 'Support',
      'cgv': 'Terms',
      'rgpd': 'Privacy',
      'ai-policy': 'AI Policy'
    },
    'fr': {
      'home': 'Accueil',
      'office': 'MetaSaaS pour Bureaux',
      'resto': 'MetaSaaS pour Restaurants',
      'pricing': 'Tarifs',
      'compare': 'Comparer les Solutions',
      'contact': 'Contact',
      'about': 'À propos du MetaSaaS',
      'careers': 'Carrières',
      'blog': 'Blog',
      'press': 'Presse',
      'team': 'Équipe',
      'support': 'Support',
      'cgv': 'CGV',
      'rgpd': 'RGPD',
      'ai-policy': 'Politique IA'
    },
    'es': {
      'home': 'Inicio',
      'office': 'MetaSaaS para Oficinas',
      'resto': 'MetaSaaS para Restaurantes',
      'pricing': 'Precios',
      'compare': 'Comparar Soluciones',
      'contact': 'Contacto',
      'about': 'Acerca del MetaSaaS',
      'careers': 'Carreras',
      'blog': 'Blog',
      'press': 'Prensa',
      'team': 'Equipo',
      'support': 'Soporte',
      'cgv': 'Términos',
      'rgpd': 'Privacidad',
      'ai-policy': 'Política IA'
    },
    'de': {
      'home': 'Startseite',
      'office': 'MetaSaaS für Büros',
      'resto': 'MetaSaaS für Restaurants',
      'pricing': 'Preise',
      'compare': 'Lösungen Vergleichen',
      'contact': 'Kontakt',
      'about': 'Über MetaSaaS',
      'careers': 'Karriere',
      'blog': 'Blog',
      'press': 'Presse',
      'team': 'Team',
      'support': 'Support',
      'cgv': 'AGB',
      'rgpd': 'Datenschutz',
      'ai-policy': 'KI-Richtlinie'
    },
    'it': {
      'home': 'Home',
      'office': 'MetaSaaS per Uffici',
      'resto': 'MetaSaaS per Ristoranti',
      'pricing': 'Prezzi',
      'compare': 'Confronta Soluzioni',
      'contact': 'Contatto',
      'about': 'Chi siamo MetaSaaS',
      'careers': 'Carriere',
      'blog': 'Blog',
      'press': 'Stampa',
      'team': 'Team',
      'support': 'Supporto',
      'cgv': 'Termini',
      'rgpd': 'Privacy',
      'ai-policy': 'Politica IA'
    }
  };
  
  return translations[lang] || translations['en'];
};

const toTitle = (segment: string, lang: string) => {
  const translations = getBreadcrumbTranslations(lang);
  return translations[segment] || segment.charAt(0).toUpperCase() + segment.slice(1);
};

// Vérifier si la page courante doit avoir un breadcrumb
const shouldShowBreadcrumb = (pathname: string): boolean => {
  // Exclure les pages système/techniques
  if (pathname.includes('/admin') || 
      pathname.includes('/success') || 
      pathname.includes('/test-liaison') ||
      pathname.includes('/plasmic')) {
    return false;
  }
  
  // Extraire le segment principal (sans langue)
  const cleanPath = pathname.replace(/^\/[a-z]{2}(?:-[a-z]{2})?/, '');
  const mainSegment = cleanPath.split('/').filter(Boolean)[0];
  
  // Autoriser la page d'accueil
  if (!mainSegment) return true;
  
  // Vérifier si c'est une page publique
  return PUBLIC_PAGES.includes(mainSegment);
};

export const BreadcrumbsJSONLD: React.FC = () => {
  const { i18n } = useTranslation();
  
  const data = useMemo(() => {
    const currentPath = window.location.pathname;
    
    // Vérifier si cette page doit avoir un breadcrumb
    if (!shouldShowBreadcrumb(currentPath)) {
      return null;
    }
    
    const origin = window.location.origin;
    
    // 🌍 Détecter la langue depuis l'URL
    const langMatch = currentPath.match(/^\/([a-z]{2}(?:-[a-z]{2})?)\//i);
    const currentLang = langMatch ? langMatch[1] : 'en';
    
    // Extraire le chemin sans langue et nettoyer les query params
    const path = currentPath.replace(/^\/[a-z]{2}(?:-[a-z]{2})?/, '').split('?')[0];
    const parts = path.split('/').filter(Boolean);
    
    // 🌍 Home avec langue + trailing slash + traduction
    const homeUrl = langMatch ? `${origin}/${currentLang}/` : `${origin}/`;
    const homeName = toTitle('home', currentLang);
    const items = [{ position: 1, name: homeName, item: homeUrl }];
    
    // 🌍 Construire le chemin avec langue
    let acc = langMatch ? `/${currentLang}` : '';
    parts.forEach((seg, idx) => {
      // Vérifier que le segment est une page publique
      if (idx === 0 && !PUBLIC_PAGES.includes(seg)) {
        return; // Skip si pas une page publique
      }
      
      acc += '/' + seg;
      
      // 🎯 Traitement spécial pour articles de blog
      let segmentName = toTitle(seg, currentLang);
      if (idx === 1 && parts[0] === 'blog') {
        // Pour les articles de blog, utiliser le titre de la page au lieu du slug
        segmentName = document.title.split('|')[0].trim() || segmentName;
      }
      
      // URLs canoniques sans paramètres + trailing slash
      const itemUrl = `${origin}${acc}/`;
      items.push({ 
        position: idx + 2, 
        name: segmentName,
        item: itemUrl 
      });
    });
    
    return {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: items.map((it) => ({
        '@type': 'ListItem',
        position: it.position,
        name: it.name,
        item: it.item
      }))
    };
  }, [i18n.language]);

  // Ne pas rendre si pas de données (page exclue)
  if (!data) {
    return null;
  }

  return (
    <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(data) }} />
  );
}; 